import React, { useState, useEffect } from 'react';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  History,
  AlertTriangle,
  CheckCircle,
  Clock,
  Calendar
} from 'lucide-react';
import { componentService, getStatusConfig } from '../../services/componentService';
import ComponentModal from './ComponentModal';
import ComponentHistoryModal from './ComponentHistoryModal';
import { useDateFormat } from '../../hooks/useDateFormat';

const ComponentsTab = ({ assetId, assetName }) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const { formatDate } = useDateFormat();

  useEffect(() => {
    if (assetId) {
      loadComponents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [assetId]);

  const loadComponents = async () => {
    setLoading(true);
    try {
      const data = await componentService.getAssetComponents(assetId);
      setComponents(data || []);
    } catch (error) {
      console.error('Error loading components:', error);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingComponent(null);
    setShowModal(true);
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this component?')) {
      try {
        await componentService.deleteComponent(id);
        loadComponents();
      } catch (error) {
        console.error('Error deleting component:', error);
      }
    }
  };

  const handleViewHistory = (componentId) => {
    setSelectedComponentId(componentId);
    setShowHistoryModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingComponent(null);
    loadComponents();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'OVERDUE':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'DUE_SOON':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'ACTIVE':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Package className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Replaceable Components</h3>
          <p className="text-sm text-gray-500">Track parts that need periodic replacement</p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Component
        </button>
      </div>

      {/* Components List */}
      {components.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No components added yet</p>
          <button
            onClick={handleCreate}
            className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
          >
            Add your first component
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Component</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {components.map((component) => {
                const statusConfig = getStatusConfig(component.status);
                return (
                  <tr key={component.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{component.name}</div>
                          {component.templateName && (
                            <div className="text-xs text-gray-500">{component.templateName}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {component.partNumber || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(component.installationDate)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{formatDate(component.nextDueDate)}</span>
                        {component.daysUntilDue !== null && (
                          <span className={`text-xs ${component.isOverdue ? 'text-red-600' : component.isDueSoon ? 'text-yellow-600' : 'text-gray-500'}`}>
                            ({component.isOverdue ? `${Math.abs(component.daysUntilDue)} days overdue` : `${component.daysUntilDue} days`})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bgClass}`}>
                        {getStatusIcon(component.status)}
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewHistory(component.id)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(component)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(component.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Component Modal */}
      {showModal && (
        <ComponentModal
          assetId={assetId}
          component={editingComponent}
          onClose={handleModalClose}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <ComponentHistoryModal
          componentId={selectedComponentId}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedComponentId(null);
          }}
        />
      )}
    </div>
  );
};

export default ComponentsTab;
